import {
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  BarChart,
  AreaChart,
  Tooltip,
  TooltipProps,
  PieChart,
  ComposedChart,
} from "recharts";
import { ChartTypes } from "./types";
import { ComponentProps, useState } from "react";
import classes from "./chart.module.scss";
import { AxisInterval } from "recharts/types/util/types";
import { FullscreenIcon } from "@assets/icons";
import { IconButton, Stack } from "@altimateai/lego";
import ChartFullScreenDialog from "./ChartFullScreenDialog";
import { yAxisLabelProps } from "./constants";

type ChartsByTypeProps = { type: ChartTypes } & Record<string, unknown>;

const ChartsByType = ({ type, children, ...rest }: ChartsByTypeProps) => {
  switch (type) {
    case ChartTypes.LineChart:
      return <LineChart {...rest}>{children}</LineChart>;
    case ChartTypes.BarChart:
      return <BarChart {...rest}>{children}</BarChart>;
    case ChartTypes.ComposedChart:
      return <ComposedChart {...rest}>{children}</ComposedChart>;
    case ChartTypes.AreaChart:
      return <AreaChart {...rest}>{children}</AreaChart>;
    case ChartTypes.PieChart:
      return <PieChart {...rest}>{children}</PieChart>;
  }
};

const CustomYAxisTick = ({
  x,
  y,
  payload,
  yAxisLabelFormatter,
}: {
  x: number;
  y: number;
  payload: Record<string, string>;
  yAxisLabelFormatter?: (value: string) => string;
}) => (
  <g transform={`translate(${x},${y})`}>
    <text textAnchor="end" dy={5} fill="#66768D">
      {yAxisLabelFormatter ? yAxisLabelFormatter(payload.value) : payload.value}
    </text>
  </g>
);

// Utility to truncate long labels with ellipsis
function truncateLabel(label: string, maxLength = 12) {
  return label.length > maxLength ? label.slice(0, maxLength) + "…" : label;
}

const CustomXAxisTick = ({
  x,
  y,
  payload,
  xAxisLabelFormatter,
}: {
  x?: number;
  y?: number;
  payload?: Record<string, string>;
  xAxisLabelFormatter?: (value: string) => string;
}) =>
  payload ? (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dx={0}
        dy={4}
        transform="rotate(90)"
        textAnchor="start"
        fill="#66768D"
        style={{ fontSize: 12 }}
      >
        <title>{xAxisLabelFormatter ? xAxisLabelFormatter(payload.value) : payload.value}</title>
        {xAxisLabelFormatter
          ? truncateLabel(xAxisLabelFormatter(payload.value))
          : truncateLabel(payload.value)}
      </text>
    </g>
  ) : null;

const getXAxisInterval = (length: number, interval?: AxisInterval) =>
  interval ?? (length > 100 ? Math.round(length / 10) : 0);

const ChartComponent = ({
  data,
  type,
  children,
  xAxisDataKey,
  xAxisLabel,
  yAxisId,
  yAxisLabel,
  xAxisLabelFormatter,
  yAxisLabelFormatter,
  tooltipProps,
  height = 400,
  xAxisInterval,
  chartProps = {},
  legendProps,
  hideLegend = false,
  adaptiveBarSize = false,
  xAxisHeight,
  hideGridLines = false,
  yAxisProps,
}: Props) => {
  const [selectedLegend, setSelectedLegend] = useState<string | undefined>();
  const [barSize, setBarSize] = useState(7);

  const onRender = (ref: HTMLDivElement) => {
    if (ref) {
      const width = ref.getBoundingClientRect().width;
      const bandWidth = width / (data?.length || 1);
      // adaptiveBarSize falls back to a proportional gap on narrow bands so bars stay visible.
      setBarSize(
        adaptiveBarSize
          ? Math.max(1, Math.floor(Math.max(bandWidth - 10, bandWidth * 0.8)))
          : bandWidth - 10
      );
    }
  };

  return (
    <ResponsiveContainer width="100%" height="100%" ref={onRender} minHeight={height || 400}>
      <ChartsByType
        type={type}
        data={data}
        height={height}
        margin={{ top: 25, bottom: 25, right: 5 }}
        {...chartProps}
      >
        {type === ChartTypes.PieChart ? null : (
          <>
            {!hideGridLines && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis
              dataKey={xAxisDataKey}
              interval={getXAxisInterval((data || []).length, xAxisInterval)}
              // Explicit band height for rotated/long labels; undefined uses the recharts default.
              height={xAxisHeight}
              label={xAxisLabel}
              tick={<CustomXAxisTick xAxisLabelFormatter={xAxisLabelFormatter} />}
            />
            <YAxis
              // Spread FIRST so a caller can add axis options without being
              // able to clobber the id, label or tick renderer this
              // component owns.
              {...yAxisProps}
              yAxisId={yAxisId}
              label={{ ...yAxisLabelProps, ...yAxisLabel }}
              tick={
                //   @ts-expect-error other values will be injected
                <CustomYAxisTick yAxisLabelFormatter={yAxisLabelFormatter} />
              }
            />
          </>
        )}
        <Tooltip {...tooltipProps} />
        {hideLegend ? null : (
          <Legend
            align="center"
            verticalAlign="bottom"
            // Cap the legend instead of fixing its height: recharts subtracts the
            // wrapper's measured offsetHeight from the chart height, so a legend
            // with many series collapses the plot area to zero. maxHeight lets a
            // short legend stay short while bounding a long one.
            wrapperStyle={{ bottom: -24, maxHeight: 72, overflowY: "auto" }}
            onClick={data =>
              setSelectedLegend(prev => (prev === data.dataKey ? "" : (data.dataKey as string)))
            }
            formatter={value => <span className={classes.legend}>{value}</span>}
            {...legendProps}
          />
        )}
        {children?.({ barSize, selectedLegend })}
      </ChartsByType>
    </ResponsiveContainer>
  );
};

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any[];
  type: ChartTypes;
  children?: ({
    barSize,
    selectedLegend,
  }: {
    barSize: number;
    selectedLegend?: string;
  }) => JSX.Element;
  xAxisLabelFormatter?: (value: string) => string;
  yAxisLabelFormatter?: (value: string) => string;
  xAxisDataKey: string;
  xAxisLabel?: Record<string, unknown>;
  yAxisId?: string;
  yAxisLabel?: Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltipProps?: TooltipProps<any, any>;
  title: string;
  // Optional caption rendered as an info-icon tooltip beside the full-screen
  // title, so a chart's data caveat survives enlarging (the dialog mounts only
  // the title + chart, not any sibling the page drew next to the chart).
  info?: string;
  height?: number;
  xAxisInterval?: AxisInterval;
  hideFullscreenButton?: boolean;
  chartProps?: Record<string, unknown>;
  legendProps?: Record<string, unknown>;
  hideLegend?: boolean;
  // Size bars proportional to the category band so they stay visible with dense data.
  adaptiveBarSize?: boolean;
  // Explicit XAxis band height (px) for rotated/long labels; undefined uses the recharts default.
  xAxisHeight?: number;
  // props of iconbutton component
  fullScreenProps?: ComponentProps<typeof IconButton>;
  // Suppresses the dashed grid for charts whose fills carry the visual weight
  hideGridLines?: boolean;
  // Spread onto the y-axis (e.g. ``{ allowDecimals: false }`` for count series)
  yAxisProps?: Record<string, unknown>;
}

const Chart = (props: Props) => {
  const [fullscreen, setFullscreen] = useState(false);

  const handleFullScreenClick = () => {
    setFullscreen(prev => !prev);
  };

  return (
    <>
      {fullscreen ? (
        <ChartFullScreenDialog
          open={fullscreen}
          onClose={handleFullScreenClick}
          title={props.title}
          info={props.info}
        >
          <ChartComponent {...props} />
        </ChartFullScreenDialog>
      ) : null}
      <div
        className={classes.chart}
        style={{ minHeight: props.height || 400 }}
        aria-label={`${props.title} chart`}
      >
        <ChartComponent {...props} />
        {props.hideFullscreenButton ? null : (
          <IconButton
            variant="ghost"
            onClick={handleFullScreenClick}
            {...props.fullScreenProps}
            title={props.fullScreenProps?.title || "Show full screen"}
            className={`!al-p-0 ${classes.fullscreen} ${props.fullScreenProps?.className}`}
          >
            <FullscreenIcon className="!al-w-[24px] !al-h-[24px]" />
          </IconButton>
        )}
        {props.data?.length ? null : (
          <Stack className={classes.no_data} aria-label="No data available">
            No data available
          </Stack>
        )}
      </div>
    </>
  );
};

export default Chart;
